import { updateLessonItem } from 'core/crud/contents.crud';
import { useTranslate } from 'core/hooks/useTranslate';
import { successResponse } from 'core/utils/utils';
import uniqueId from 'lodash/uniqueId';
import { useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import { usePopover } from 'ui';

export function useAvatarsList({ showDropdown, onClick, onSaveAssignedStudents, allStudents, ariaLabel, lessonItemGuid, courseGuid, avatars }) {
  const { addToast } = useToasts();
  const { t } = useTranslate();
  const { anchorEl, dismiss, getTriggerProps, isOpen, toggle } = usePopover();
  const [triggerId] = useState(() => uniqueId('avatars-list-'));
  const [openAssignStudentsDialog, setOpenAssignStudentsDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const isDropdownMode = !onClick && showDropdown;

  function getDropdownTriggerProps() {
    if (!isDropdownMode) return {};
    // Sin el `onClick` de `getTriggerProps()`: el click lo resuelve `handleClickComponent`, que
    // además frena el evento para que no llegue a un contenedor clicable (tarjeta, enlace).
    const { 'aria-expanded': ariaExpanded, 'aria-haspopup': ariaHasPopup, ref } = getTriggerProps();
    return { 'aria-expanded': ariaExpanded, 'aria-haspopup': ariaHasPopup, ref };
  }

  function handleClickComponent(e) {
    if (onClick) {
      onClick();
    } else if (showDropdown) {
      e.preventDefault();
      e.stopPropagation();
      toggle();
    } else if (onSaveAssignedStudents && allStudents) {
      setOpenAssignStudentsDialog(true);
    }
  }

  function handleCloseAssignStudentsDialog() {
    setOpenAssignStudentsDialog(false);
  }

  async function handleSaveAssignedStudents(students) {
    setLoading(true);
    try {
      const selectedStudents = students.filter((student) => student.selected);
      const newStudents = selectedStudents.map((student) => student.person_guid);
      const itemFor = selectedStudents.length === allStudents.length ? 'all' : 'some-users';
      const payload = {
        guid: lessonItemGuid,
        course_guid: courseGuid,
        item_for: itemFor,
        item_for_users: newStudents,
      };
      const response = await updateLessonItem(payload);
      if (successResponse(response)) {
        onSaveAssignedStudents({ assignedTo: selectedStudents, item_for: itemFor, item_for_users: newStudents, assignedUsers: selectedStudents });
      } else {
        addToast(t('lesson:Could not update resource'), {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }

    handleCloseAssignStudentsDialog();
  }

  function getMainAriaLabel() {
    if (ariaLabel) return ariaLabel;
    return t('common:X assigned students', { count: avatars.length }) + ': ' + avatars.map((avatar) => avatar.name).join(', ');
  }

  return {
    anchorEl,
    handleClickComponent,
    handleCloseAssignStudentsDialog,
    handleCloseDropdown: dismiss,
    handleSaveAssignedStudents,
    getMainAriaLabel,
    isDropdownMode,
    loading,
    openAssignStudentsDialog,
    openDropdown: isOpen,
    triggerId,
    triggerProps: getDropdownTriggerProps(),
  };
}
